// frontend/src/pages/doctor/OnlineConsultation.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Video, Phone, MessageSquare, FileText, Save, X } from 'lucide-react';
import doctorService from '../../services/doctorService';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const OnlineConsultation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [consultation, setConsultation] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [notes, setNotes] = useState('');

  const [completionData, setCompletionData] = useState({
    diagnosis: '',
    prescription: [''],
    recommendations: '',
    followUpDate: '',
    doctorNotes: ''
  });

  useEffect(() => {
    fetchConsultation();
  }, [id]);

  const fetchConsultation = async () => {
    try {
      setLoading(true);
      const response = await apiService.consultations.getById(id);
      
      if (response.success) {
        setConsultation(response.data);
        setNotes(response.data.doctorNotes || '');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch consultation');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletionChange = (field, value) => {
    setCompletionData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrescriptionChange = (index, value) => {
    const newPrescription = [...completionData.prescription];
    newPrescription[index] = value;
    setCompletionData(prev => ({ ...prev, prescription: newPrescription }));
  };

  const addPrescriptionLine = () => {
    setCompletionData(prev => ({
      ...prev,
      prescription: [...prev.prescription, '']
    }));
  };

  const handleComplete = async () => {
    if (!completionData.diagnosis.trim()) {
      alert('Please provide a diagnosis');
      return;
    }

    try {
      const response = await doctorService.completeConsultation(id, {
        ...completionData,
        prescription: completionData.prescription.filter(med => med.trim()),
        doctorNotes: notes
      });

      if (response.success) {
        alert('Consultation completed successfully!');
        navigate('/doctor/consultations');
      }
    } catch (err) {
      alert(err.message || 'Failed to complete consultation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
        {error || 'Consultation not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Online Consultation</h1>
          <p className="text-gray-600 mt-1">
            {consultation.patient?.name || 'Anonymous Patient'}
          </p>
        </div>
        <button
          onClick={() => navigate('/doctor/consultations')}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          <X className="w-4 h-4 mr-2" />
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Video Call Section */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="aspect-video bg-gray-900 flex items-center justify-center">
              <div className="text-center text-white">
                <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">Video Call Integration</p>
                <p className="text-sm text-gray-400">Connect your preferred video call service</p>
                <div className="mt-4 space-x-3">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg inline-flex items-center">
                    <Video className="w-4 h-4 mr-2" />
                    Start Video
                  </button>
                  <button className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg inline-flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    Start Audio
                  </button>
                </div>
              </div>
            </div>

            {/* Call Controls */}
            <div className="bg-gray-50 p-4 flex justify-center space-x-4">
              <button className="p-3 bg-gray-200 hover:bg-gray-300 rounded-full">
                <Video className="w-5 h-5 text-gray-700" />
              </button>
              <button className="p-3 bg-gray-200 hover:bg-gray-300 rounded-full">
                <Phone className="w-5 h-5 text-gray-700" />
              </button>
              <button className="p-3 bg-red-600 hover:bg-red-700 rounded-full">
                <Phone className="w-5 h-5 text-white transform rotate-135" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {[
                  { id: 'details', label: 'Patient Details' },
                  { id: 'diagnosis', label: 'Diagnosis & Prescription' },
                  { id: 'notes', label: 'Notes' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === tab.id
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'details' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Symptoms</h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">
                      {consultation.symptoms}
                    </p>
                  </div>

                  {consultation.patientNotes && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Patient Notes</h4>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">
                        {consultation.patientNotes}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Type</h4>
                      <p className="text-gray-700 capitalize">{consultation.type}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Fee</h4>
                      <p className="text-gray-700">KES {consultation.fee.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'diagnosis' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Diagnosis *
                    </label>
                    <textarea
                      value={completionData.diagnosis}
                      onChange={(e) => handleCompletionChange('diagnosis', e.target.value)}
                      rows="4"
                      placeholder="Provide detailed diagnosis..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Prescription
                      </label>
                      <button
                        onClick={addPrescriptionLine}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        + Add Medicine
                      </button>
                    </div>
                    <div className="space-y-2">
                      {completionData.prescription.map((med, index) => (
                        <input
                          key={index}
                          type="text"
                          value={med}
                          onChange={(e) => handlePrescriptionChange(index, e.target.value)}
                          placeholder="Medicine, dosage, frequency, duration"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recommendations
                    </label>
                    <textarea
                      value={completionData.recommendations}
                      onChange={(e) => handleCompletionChange('recommendations', e.target.value)}
                      rows="3"
                      placeholder="Lifestyle advice, follow-up instructions..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Follow-up Date
                    </label>
                    <input
                      type="date"
                      value={completionData.followUpDate}
                      onChange={(e) => handleCompletionChange('followUpDate', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'notes' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Private Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="10"
                    placeholder="Your private consultation notes..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    These notes are private and will not be shared with the patient
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Patient Info Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Patient Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium text-gray-900">
                  {consultation.patient?.name || 'Anonymous'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-medium text-gray-900">
                  {consultation.patient?.phone || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900">
                  {consultation.patient?.email || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                <MessageSquare className="w-4 h-4 mr-2" />
                Send Message
              </button>
              <button className="w-full flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100">
                <FileText className="w-4 h-4 mr-2" />
                View History
              </button>
              <button
                onClick={handleComplete}
                disabled={!completionData.diagnosis.trim()}
                className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                Complete Consultation
              </button>
            </div>
          </div>

          {/* Consultation Status */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">Status</h4>
            <p className="text-sm text-yellow-700">
              Consultation in progress. Complete all sections before ending the session.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlineConsultation;